import React, { useEffect, useMemo } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Check } from "lucide-react";
import Seo from "../components/Seo";
import { Header } from "../components/TopSections";
import { Footer } from "../components/BottomSections";
import { SERVICE_PAGES, SERVICE_ORDER } from "../data/servicePages";

const buildJsonLd = (data) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: data.h1,
  description: data.description,
  areaServed: "Puerto Vallarta, Jalisco, México",
  provider: {
    "@type": "LegalService",
    name: "Contreras & Asociados PV",
    telephone: "+523346240169",
    email: "info@contrerasasociados.online",
  },
});

const ServiceHero = ({ data }) => (
  <section className="svc-hero">
    <div className="wrap">
      <div className="crumb">
        <Link to="/">Inicio</Link> &nbsp;/&nbsp; {data.nav}
      </div>
      <div className="eyebrow">
        <span className="diamond">◆</span> {data.eyebrow}
      </div>
      <h1>{data.h1}</h1>
      {data.intro[0] && <p>{data.intro[0]}</p>}
    </div>
  </section>
);

const ServiceBody = ({ data }) => {
  const extraParagraphs = useMemo(() => data.intro.slice(1), [data]);
  return (
    <section className="svc-body">
      <div className="wrap svc-grid2">
        <div>
          {extraParagraphs.map((p) => (
            <p className="lead-p" key={p}>
              {p}
            </p>
          ))}
          <ul className="svc-includes">
            {data.includes.map((item) => (
              <li key={item}>
                <Check size={18} strokeWidth={2.4} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="lead-p" style={{ marginTop: "28px" }}>
            {data.closing}
          </p>
        </div>
        <ConsultaAside />
      </div>
    </section>
  );
};

const ConsultaAside = () => (
  <aside className="svc-aside">
    <h3>Consulta gratuita</h3>
    <p>
      Cuéntanos tu caso y un abogado del despacho te contactará en menos de 24
      horas. Atendemos en español e inglés.
    </p>
    <a href="/#contacto" className="btn btn-solid">
      Agenda tu consulta
    </a>
    <p style={{ marginTop: "18px", marginBottom: 0 }}>
      O llámanos:{" "}
      <a href="tel:+523346240169" style={{ color: "var(--green)" }}>
        +52 33 4624 0169
      </a>
    </p>
  </aside>
);

const RelatedServices = ({ slug }) => {
  const related = useMemo(
    () => SERVICE_ORDER.filter((s) => s !== slug),
    [slug]
  );
  return (
    <section className="svc-related">
      <div className="wrap">
        <h2>Otras áreas de práctica</h2>
        <div className="svc-related-grid">
          {related.map((s) => (
            <Link key={s} to={`/${s}`}>
              {SERVICE_PAGES[s].nav}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const ServicePage = () => {
  const { slug } = useParams();
  const data = SERVICE_PAGES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const jsonLd = useMemo(() => (data ? buildJsonLd(data) : null), [data]);

  if (!data) return <Navigate to="/" replace />;

  return (
    <>
      <Seo
        title={data.title}
        description={data.description}
        keywords={data.keywords}
        path={`/${data.slug}`}
        jsonLd={jsonLd}
      />
      <Header />
      <main>
        <ServiceHero data={data} />
        <ServiceBody data={data} />
        <RelatedServices slug={slug} />
      </main>
      <Footer />
    </>
  );
};

export default ServicePage;
